<template>
  <SingleColumn>
    <template #header>
      <section class="sm:flex sm:items-center sm:justify-between">
        <div class="sm:flex sm:space-x-5">
          <h1 class="text-3xl font-bold text-white">
            Lass dich inspirieren
          </h1>
        </div>
        <div class="mt-5 flex justify-center sm:mt-0">
          <SelectOrdering v-model="option_ordering" />
        </div>
      </section>
    </template>
    <template #content>
      <section class="pt-6">
        <h2 id="products-heading" class="sr-only">
          Filter
        </h2>
        <div class="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-4 ">
          <form class="block">
            <FilterPracticeStatus v-model="option_status" class="mb-2" />
            <FilterPracticeKeywords v-model="option_keywords" class="mb-2" />
            <!-- <FilterPracticeCommitment v-model="option_commitment" class="mb-2" /> -->
            <FilterPracticeExpansion v-model="option_expansion" class="mb-2" />
            <!-- <FilterPracticeInvestment v-model="option_investment" class="mb-2" /> -->
            <FilterPracticeRelevance v-model="option_relevance" class="mb-2" />
          </form>
          <div class="lg:col-span-3">
            <div v-if="empty" class="h-96 lg:h-full">
              <div class="bg-white shadow overflow-hidden sm:rounded-lg">
                <div class="px-4 py-5 sm:px-6">
                  <p class="mt-1 max-w-2xl text-sm text-gray-500">
                    Für diese Auswahl gibt es keine Methoden. Probiere andere Filter aus.
                  </p>
                </div>
              </div>
            </div>
            <div v-else="empty" class="block">
              <ul role="list" class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3">
                <PracticeCard v-for="practice in data" :key="practice.id" :practice="practice" as="li" class="col-span-1"/>
              </ul>
              <div v-if="more" class="block mt-6">
                <button @click="load" type="button" class="button-more">
                  Mehr Methoden anzeigen
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>
  </SingleColumn>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import _ from "lodash";
import client from '@/client';

// Default orderings
const DEFAULT_ORDER_FREE = 'effort';
const DEFAULT_ORDER_PAID = 'advice';

// Resolve default ordering
const resolve_order = (p) => p ? DEFAULT_ORDER_PAID : DEFAULT_ORDER_FREE;

// Use the store
const store = useStore();

// Define references
const data = ref(new Array);
const meta = ref(new Object);

// Define computed properties
const context = computed(() => store.getters['context/context']);
const parent = computed(() => context.value.id);
const premium = computed(() => context.value.active);

const empty = computed(() => _.isEmpty(data.value));
const page = computed(() => meta.value.current_page ?? 1);
const last = computed(() => meta.value.last_page ?? 1);
const more = computed(() => page.value < last.value);

// Define methods and on mount action
const mount = onMounted(() => fetch(0));
const load = () => extend(page.value+1);
const apply = () => fetch(0);

// Define option references
const option_ordering = ref(resolve_order(premium.value));
const option_status = ref(new Array);
const option_keywords = ref(new Array);
const option_commitment = ref(new Array);
const option_expansion = ref(new Array);
const option_investment = ref(new Array);
const option_relevance = ref(new Array);

// Define option watchers
const watch_ordering = watch(option_ordering, (v, _) => filter());
const watch_status = watch(option_status, (v, _) => filter());
const watch_keywords = watch(option_keywords, (v, _) => filter());
const watch_commitment = watch(option_commitment, (v, _) => filter());
const watch_expansion = watch(option_expansion, (v, _) => filter());
const watch_investment = watch(option_investment, (v, _) => filter());
const watch_relevance = watch(option_relevance, (v, _) => filter());
const watch_parent = watch(parent, (v, _) => filter());

const prepare = () =>
{
  const result = new Object;
  result.order = option_ordering.value;
  result.status = option_status.value;
  result.keywords = option_keywords.value;
  result.commitment = option_commitment.value;
  result.expansion = option_expansion.value;
  result.investment = option_investment.value;
  result.relevance = option_relevance.value;
  return result;
};

const fetch = (page=1) =>
{
  const target = '/api/podcasts/' + parent.value + '/practices';
  const params = {params: {page: page}};
  client.get(target, params)
  .then(result =>
  {
    data.value = result.data.data;
    meta.value = result.data.meta;
  })
  .catch(error =>
  {
    data.value = new Array;
    meta.value = new Object;
  });
};

const extend = (page=1) =>
{
  const target = '/api/podcasts/' + parent.value + '/practices';
  const params = {params: _.merge({page: page}, [prepare()])};
  client.get(target, params)
  .then(result =>
  {
    data.value = data.value.concat(result.data.data);
    meta.value = result.data.meta;
  })
  .catch(error =>
  {
    data.value = new Array;
    meta.value = new Object;
  });
};

const filter = () =>
{
  const target = '/api/podcasts/' + parent.value + '/practices';
  const params = {params: prepare()};
  client.get(target, params)
  .then(result =>
  {
    data.value = result.data.data;
    meta.value = result.data.meta;
  })
  .catch(error =>
  {
    data.value = new Array;
    meta.value = new Object;
  });
};

</script>

<style scoped>
.button-apply
{
  @apply flex justify-center items-center px-4 py-2;
  @apply border border-gray-300 shadow-sm;
  @apply text-sm font-medium rounded-md text-gray-700;
  @apply bg-white hover:bg-gray-50;
}

.button-more
{
  @apply relative block w-full p-4 rounded-lg;
  @apply text-center text-sm font-medium text-gray-600;
  @apply bg-gray-100 hover:bg-gray-200 hover:text-gray-800;
  @apply focus:outline-none focus:ring-2 focus:ring-teal-500;
}
</style>

